import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  MenuItem,
  Pagination,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import ExpandMoreRoundedIcon from "@mui/icons-material/ExpandMoreRounded";
import RefreshRoundedIcon from "@mui/icons-material/RefreshRounded";
import AppButton from "../../components/button/AppButton";
import AppTextInput from "../../components/inputs/AppTextInput";
import AppSelectInput from "../../components/inputs/AppSelectInput";
import { formatDateTime } from "../../utils/helperFunctions/dateFormatter";
import { readableText } from "../../utils/helperFunctions/stringFormatter";
import { useActivityLogsScreen } from "./hooks/useActivityLogsScreen";

const methodColor = (method?: string | null) => {
  if (method === "GET") {
    return "primary";
  }

  if (method === "POST") {
    return "success";
  }

  if (method === "PATCH" || method === "PUT") {
    return "warning";
  }

  if (method === "DELETE") {
    return "error";
  }

  return "default";
};

const ActivityLogsScreen = () => {
  const {
    logs,
    pagination,
    filters,
    loading,
    error,
    updateFilter,
    refreshLogs,
  } = useActivityLogsScreen();

  return (
    <Stack spacing={3}>
      <Box
        sx={{
          display: "flex",
          gap: 2,
          justifyContent: "space-between",
          alignItems: { xs: "flex-start", md: "center" },
          flexDirection: { xs: "column", md: "row" },
        }}
      >
        <Box>
          <Typography variant="h4" sx={{ fontWeight: 900 }}>
            Activity logs
          </Typography>
          <Typography color="text.secondary">
            Human-readable record of actions from mobile, web and admin panel.
          </Typography>
        </Box>

        <AppButton
          variant="outlined"
          startIcon={<RefreshRoundedIcon />}
          onClick={refreshLogs}
          disabled={loading}
        >
          Refresh
        </AppButton>
      </Box>

      {error ? <Alert severity="error">{error}</Alert> : null}

      <Card>
        <CardContent>
          <Stack spacing={2}>
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: {
                  xs: "1fr",
                  md: "2fr 1fr 1fr",
                },
                gap: 2,
              }}
            >
              <AppTextInput
                label="Search"
                value={filters.search || ""}
                onChange={(value) => updateFilter("search", value)}
                placeholder="Search description, action, path, user, IP..."
              />

              <AppSelectInput
                label="Source"
                value={filters.source || ""}
                onChange={(value) => updateFilter("source", value)}
                options={[
                  { label: "All sources", value: "" },
                  { label: "Admin panel", value: "admin-panel" },
                  { label: "Web app", value: "web-app" },
                  { label: "Mobile app", value: "mobile-app" },
                  { label: "Unknown", value: "unknown" },
                ]}
              />

              <AppTextInput
                label="Action"
                value={filters.action || ""}
                onChange={(value) => updateFilter("action", value)}
                placeholder="admin.account.created"
              />
            </Box>

            {loading ? (
              <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
                <CircularProgress />
              </Box>
            ) : (
              <Box sx={{ overflowX: "auto" }}>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>Time</TableCell>
                      <TableCell>Description</TableCell>
                      <TableCell>Actor</TableCell>
                      <TableCell>Account</TableCell>
                      <TableCell>Source</TableCell>
                      <TableCell>Method</TableCell>
                      <TableCell>Status</TableCell>
                    </TableRow>
                  </TableHead>

                  <TableBody>
                    {logs.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={7}>
                          <Typography
                            color="text.secondary"
                            sx={{ textAlign: "center" }}
                          >
                            No activity logs found.
                          </Typography>
                        </TableCell>
                      </TableRow>
                    ) : (
                      logs.map((log) => (
                        <TableRow key={log.id} hover>
                          <TableCell sx={{ whiteSpace: "nowrap" }}>
                            {formatDateTime(log.created_at)}
                          </TableCell>

                          <TableCell sx={{ minWidth: 420 }}>
                            <Accordion disableGutters elevation={0}>
                              <AccordionSummary
                                expandIcon={<ExpandMoreRoundedIcon />}
                                sx={{ px: 0 }}
                              >
                                <Stack spacing={0.5}>
                                  <Typography sx={{ fontWeight: 800 }}>
                                    {log.description}
                                  </Typography>
                                  <Typography
                                    variant="caption"
                                    color="text.secondary"
                                  >
                                    {log.action} · {log.path || "-"}
                                  </Typography>
                                </Stack>
                              </AccordionSummary>

                              <AccordionDetails sx={{ px: 0 }}>
                                <Box
                                  component="pre"
                                  sx={{
                                    m: 0,
                                    p: 2,
                                    bgcolor: "background.default",
                                    border: "1px solid",
                                    borderColor: "divider",
                                    borderRadius: 2,
                                    overflowX: "auto",
                                    fontSize: 12,
                                  }}
                                >
                                  {JSON.stringify(
                                    {
                                      request_data: log.request_data,
                                      user_agent: log.user_agent,
                                      ip_address: log.ip_address,
                                      target_user: log.target_user,
                                    },
                                    null,
                                    2,
                                  )}
                                </Box>
                              </AccordionDetails>
                            </Accordion>
                          </TableCell>

                          <TableCell>
                            <Stack spacing={0.3}>
                              <Typography sx={{ fontWeight: 800 }}>
                                {log.actor?.name || "-"}
                              </Typography>
                              <Typography
                                variant="caption"
                                color="text.secondary"
                              >
                                {log.actor?.email || "-"}
                              </Typography>
                            </Stack>
                          </TableCell>

                          <TableCell>{log.account?.name || "-"}</TableCell>

                          <TableCell>
                            <Chip
                              label={readableText(log.source)}
                              size="small"
                              sx={{ fontWeight: 800 }}
                            />
                          </TableCell>

                          <TableCell>
                            <Chip
                              label={log.method || "-"}
                              color={methodColor(log.method) as any}
                              size="small"
                              sx={{ fontWeight: 800 }}
                            />
                          </TableCell>

                          <TableCell>{log.response_status || "-"}</TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </Box>
            )}

            {pagination ? (
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  gap: 2,
                  flexDirection: { xs: "column", md: "row" },
                }}
              >
                <Typography variant="body2" color="text.secondary">
                  Showing {pagination.from || 0}–{pagination.to || 0} of{" "}
                  {pagination.total}
                </Typography>

                <Stack
                  direction="row"
                  spacing={2}
                  sx={{
                    alignItems: "center",
                  }}
                >
                  <Select
                    size="small"
                    value={String(filters.per_page || 30)}
                    onChange={(event) =>
                      updateFilter("per_page", Number(event.target.value))
                    }
                  >
                    <MenuItem value="10">10</MenuItem>
                    <MenuItem value="30">30</MenuItem>
                    <MenuItem value="50">50</MenuItem>
                  </Select>

                  <Pagination
                    page={pagination.current_page}
                    count={pagination.last_page}
                    color="primary"
                    onChange={(_, page) => updateFilter("page", page)}
                  />
                </Stack>
              </Box>
            ) : null}
          </Stack>
        </CardContent>
      </Card>
    </Stack>
  );
};

export default ActivityLogsScreen;
